import { useState, useEffect } from 'react'
import s from './DealTimeTracker.scss'
import { isTimetrackerAdmin, isTimetrackerRegular, getToken } from 'utils/authService'
import { makeGetRequest, makeDeleteRequest, getBaseUrl } from 'utils/api'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { AddButton } from 'components/common/AddButton'
import { FaClock, FaClone, FaFileExcel } from 'react-icons/fa'
import swal from 'sweetalert'
import { toUsers } from './serializers'
import { LogTimeModal } from './LogTimeModal'
import MaterialTable from '@material-table/core'
import { timezoneDate } from 'utils/helpers'

const columns = [
  {
    title: 'Date Entered',
    field: 'workDate',
    render: row => timezoneDate(row.workDate)
  },
  { title: 'Name', field: 'userName', defaultSort: 'asc' },
  { title: 'Time', field: 'hours', render: row => `${row.hours} hrs` },
  {
    title: 'Work Type',
    field: 'typeOfWork',
    render: row => row.typeOfWork?.name ?? <i>multi</i>
  },
  {
    title: 'Billable',
    field: 'billable',
    render: row => {
      if (row.hasMultipleClients) return <i>multi</i>
      return row.billable ? 'Yes' : 'No'
    }
  },
  {
    title: 'Overtime',
    field: 'overtime',
    render: row => {
      if (row.hasMultipleClients) return <i>multi</i>
      return row.workTimeType === 'OVERTIME' ? 'Yes' : 'No'
    }
  },
  {
    title: 'Second Shift',
    field: 'secondShift',
    render: row => {
      if (row.hasMultipleClients) return <i>multi</i>
      return row.workTimeType === 'SECOND_SHIFT' ? 'Yes' : 'No'
    }
  },
  {
    title: 'Third Shift',
    field: 'thirdShift',
    render: row => {
      if (row.hasMultipleClients) return <i>multi</i>
      return row.workTimeType === 'THIRD_SHIFT' ? 'Yes' : 'No'
    }
  }
]

const DealTimeTracker = () => {
  const [summary, setSummary] = useState(null)
  const [rows, setRows] = useState([])
  const { id } = useParams()
  const dispatch = useDispatch()
  const [showLogTimeModal, setShowLogTimeModal] = useState(false)
  const [editedEntryId, setEditedEntryId] = useState(null)
  const [users, setUsers] = useState([])
  const navigate = useNavigate()

  const getLogs = async () => {
    try {
      const response = await makeGetRequest(`/v1/timetracker/deal/${id}`)
      setSummary(response.header)
      setRows(response.rows)
    } catch (e) {
      dispatch({
        type: 'PUSH_NOTIFICATION',
        payload: {
          title: 'There was a problem getting logs',
          level: 'error'
        }
      })
    }
  }

  useEffect(() => {
    ;(async () => {
      const { content } = await makeGetRequest('/v1/timetracker/users')
      setUsers(toUsers(content))
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!isTimetrackerAdmin() && !isTimetrackerRegular()) return
    getLogs()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const toggleLogTimeModal = () => {
    reset()
    setShowLogTimeModal(!showLogTimeModal)
  }

  const reset = () => {
    setEditedEntryId(null)
  }

  const onDelete = async (id, cb) => {
    const willRemove = await swal({
      title: 'Are you sure you want to delete this log?',
      buttons: ['No', 'Yes'],
      icon: 'warning'
    })
    if (willRemove) {
      try {
        await makeDeleteRequest(`/v1/timetracker/${id}`)
        getLogs()
        dispatch({
          type: 'PUSH_NOTIFICATION',
          payload: {
            title: 'Log deleted successfuly',
            level: 'success'
          }
        })
        cb && cb()
      } catch (e) {
        dispatch({
          type: 'PUSH_NOTIFICATION',
          payload: {
            title: 'There was a problem deleting log',
            level: 'error'
          }
        })
      }
    }
  }

  const onClone = async id => {
    const willClone = await swal({
      title: 'Are you sure you want to clone this log?',
      buttons: ['No', 'Yes'],
      icon: 'warning'
    })
    if (willClone) {
      try {
        const clonedEntry = await makeGetRequest(`/v1/timetracker/${id}/clone`)
        getLogs()
        dispatch({
          type: 'PUSH_NOTIFICATION',
          payload: {
            title: 'Log cloned successfuly',
            level: 'success'
          }
        })
        openEditModal(clonedEntry.id)
      } catch (e) {
        dispatch({
          type: 'PUSH_NOTIFICATION',
          payload: {
            title: 'There was a problem cloning log',
            level: 'error'
          }
        })
      }
    }
  }

  const openEditModal = id => {
    reset()
    setEditedEntryId(id)
    setShowLogTimeModal(true)
  }

  const onBtnExport = () => {
    window.open(
      getBaseUrl(`/v1/timetracker/deal/export/${id}?x-token-tracking=${getToken()}`),
      '_blank'
    )
  }

  if (!isTimetrackerAdmin() && !isTimetrackerRegular()) {
    navigate(`/opportunities/${id}`)
  }

  return (
    <>
      <div className={s.container}>
        <div className={s.summary}>
          <div>
            <p style={{ fontWeight: 600, fontSize: 18 }}>TOTAL {summary?.totalHours} hrs</p>
          </div>
          <div style={{ width: 150, fontWeight: 600 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Billable</span> <span>{summary?.billable} hrs</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Non-billable</span> <span>{summary?.nonBillable} hrs</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Overtime</span> <span>{summary?.overtime} hrs</span>
            </div>
          </div>
          <div style={{ width: 350, fontWeight: 500 }}>
            {summary &&
              Object.keys(summary?.byWorkType).map((key, index) => (
                <div key={index} style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>{key}</span> <span>{summary?.byWorkType[key]} hrs</span>
                </div>
              ))}
          </div>
        </div>
        <div className={s.table}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <AddButton className={s.logTimeBtn} onClick={toggleLogTimeModal}>
              <FaClock />
              Log Time
            </AddButton>
            <AddButton simpleBtn className={s.exportExcel} onClick={onBtnExport}>
              <FaFileExcel className={s.excelIcon} />
              Export to Excel
            </AddButton>
          </div>
          <MaterialTable
            title=""
            columns={columns}
            data={rows}
            options={{
              sorting: true,
              headerStyle: {
                backgroundColor: '#eee',
                color: '#000',
                position: 'sticky',
                top: 0,
                zIndex: 1,
                fontSize: '14px'
              },
              maxBodyHeight: '400px',
              pageSize: 20,
              padding: 'dense',
              pageSizeOptions: [5, 10, 20, 50, { value: rows.length, label: 'All' }]
            }}
            style={{
              boxShadow: 'none',
              fontSize: '14px'
            }}
            actions={[
              () => ({
                icon: 'edit',
                tooltip: 'Edit',
                onClick: (event, rowData) => openEditModal(rowData.id)
              }),
              () => ({
                icon: () => <FaClone />,
                tooltip: 'Clone',
                onClick: (event, rowData) => onClone(rowData.id)
              }),
              () => ({
                icon: 'delete',
                tooltip: 'Delete',
                onClick: (event, rowData) => onDelete(rowData.id)
              })
            ]}
          />
        </div>
      </div>
      {showLogTimeModal && (
        <LogTimeModal
          dealId={id}
          editedEntryId={editedEntryId}
          users={users}
          getLogs={getLogs}
          toggleLogTimeModal={toggleLogTimeModal}
          showLogTimeModal={showLogTimeModal}
          onDelete={toggleLogTimeModal => onDelete(editedEntryId, toggleLogTimeModal)}
        />
      )}
    </>
  )
}

export default DealTimeTracker
